import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { signIn, signOut, getCurrentUser, fetchUserAttributes, fetchAuthSession, SignInInput, SignInOutput } from 'aws-amplify/auth';

interface User {
  userId: string;
  email: string;
  emailVerified: boolean;
  attributes?: Record<string, string>;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (input: SignInInput) => Promise<SignInOutput>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      
      setUser({
        userId: currentUser.userId,
        email: attributes.email || '',
        emailVerified: attributes.email_verified === 'true',
        attributes: attributes as Record<string, string>,
      });
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUser();
  }, []);

  const handleSignIn = async (input: SignInInput): Promise<SignInOutput> => {
    const result = await signIn(input);
    await checkUser();
    return result;
  };

  const handleSignOut = async () => {
    await signOut();
    setUser(null);
  };

  const refreshUser = async () => {
    await checkUser();
  };

  const getIdToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      return idToken || null;
    } catch (err) {
      console.warn('Failed to get id token', err);
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn: handleSignIn,
    signOut: handleSignOut,
    refreshUser,
    getIdToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
